import type { LocaleDefinition } from "../locale-definition";

const translation = {
  actions: {
    add: "Add",
    apply: "Apply",
    back: "Back",
    cancel: "Cancel",
    clear: "Clear",
    close: "Close",
    confirm: "Confirm",
    continue: "Continue",
    create: "Create",
    delete: "Delete",
    edit: "Edit",
    remove: "Remove",
    reset: "Reset",
    retry: "Try again",
    save: "Save",
    search: "Search",
    submit: "Submit",
    signIn: "Sign in",
    rememberMe: "Remember me",
    forgotPassword: "Forgot password?",
    returnToDashboard: "Return to dashboard",
    backToLogin: "Back to login",
    sendResetInstructions: "Send reset instructions",
    resetPassword: "Reset password",
    addUser: "Add user",
    previous: "Previous",
    next: "Next",
    createUser: "Create user",
    regeneratePassword: "Regenerate password",
    backToUsers: "Back to Users",
    lock: "Lock Account",
    unlock: "Unlock Account",
    locking: "Locking...",
    unlocking: "Unlocking...",
    upload: "Upload",
    sendReminder: "Send reminder",
    updatePayee: "Update payee",
    submitUpdate: "Submit update",
    backToStores: "Back to stores",
  },

  state: {
    yes: "Yes",
    no: "No",
    none: "None",
    enabled: "Enabled",
    disabled: "Disabled",
    active: "Active",
    inactive: "Inactive",
    locked: "Locked",
    unlocked: "Unlocked",
    adding: "Adding...",
    loadingRoles: "Loading roles...",
    creating: "Creating...",
    saving: "Saving…",
    loadingStoreDetails: "Loading store details…",
    loadingPayeeDetails: "Loading payee details…",
  },

  fields: {
    emailAddress: "Email address",
    username: "Username",
    password: "Password",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    role: "Role",
    status: "Status",
    firstName: "First name",
    lastName: "Last name",
    createdAt: "CreatedAt",
    fullName: "Full Name",
    accountStatus: "Account Status",
    createdOn: "Created On",
    lastModified: "Last Modified",
    code: "Code",
    address: "Address",
    email: "Email",
    phone: "Phone",
    searchStores: "Search stores",
  },

  placeholders: {
    email: "user@example.com",
    username: "Enter your username",
    password: "Enter your password",
    newPassword: "Enter your new password",
    confirmPassword: "Confirm your new password",
    searchStores: "Search store name, code, city",
  },

  titles: {
    applicationError: "Application Error",
    pageNotFound: "Page Not Found",
    unauthorized: "Unauthorized",
    protectedWorkspace: "Protected workspace",
  },

  messages: {
    pageNotFoundDescription: "The requested page does not exist.",
    unauthorizedDescription: "You do not have permission to access this page.",
    signingIn: "Signing in...",
    signedOut: "You have been signed out.",
    signOutFailed: "You could not be signed out. Please try again.",
    sendingResetInstructions: "Sending reset instructions...",
    resettingPassword: "Resetting password...",
    userCreated: "User created successfully.",
    loadFailed: "Unable to load the user.",
    lockSuccess: "User account locked successfully.",
    unlockSuccess: "User account unlocked successfully.",
    failed: "The action could not be completed. Please try again.",
    storeNotFound: "Store not found",
    storeNotFoundHint:
      "The selected store does not exist or is no longer available.",
  },

  errors: {
    invalidCredentials: "The username or password is incorrect.",
    unauthorized: "You do not have permission to access this page.",
    roleNotFound: "The selected role does not exist.",
    roleInactive: "The selected role is inactive.",
    storeNotFound: "The requested store does not exist.",
    emptyData: "No data is available to display.",
    invalidInvitationToken: "The invitation token is invalid or has expired.",
    customerInvitationExpired: "The invitation for this customer has expired.",
    statusNotConfigured: "The status for this record is not configured.",
    payeeCreationFailed: "Unable to add the payee. Please try again.",
    customerNotFound: "The requested customer does not exist.",
    payeeNotFound: "The requested payee does not exist.",
    userNotFound: "The requested user does not exist.",
    EmptyData: "No data is available to display.",
    customerAccountAlreadyExists: "A customer account already exists.",
    userNameAlreadyExists: "This username is already in use.",
    dataInInvalidState: "The data is in an invalid state.",
    invalidProvince: "The selected province is invalid.",
    invalidPaymentMethod: "The selected payment method is invalid.",
    addressTypeNotConfigured:
      "The address type for this request is not configured.",
    payeePendingStatusNotConfigured:
      "The payee pending status for this request is not configured.",
    userLockFailed: "The user account could not be locked. Please try again.",
    UserUnlockFailed:
      "The user account could not be unlocked. Please try again.",
    errorLoadingPayeeDetails: "Unable to load payee details. Please try again.",
  },

  language: {
    label: "Language",
    switchTo: "Switch to {{language}}",
  },

  loginPage: {
    eyebrow: "MARS",

    title: "Sign in to your workspace",

    description:
      "Use your organization credentials to continue to MARS Platform.",

    welcomeBack: "Secure enterprise access",

    brandBadge: 'Store & representative workspace',

    brandTitle: 'Connect stores, representatives and payments.',


    brandDescription:
      'Manage store relationships, territory assignments and payee information in one place. Track onboarding and review payment details across your assigned stores.',

    benefits: {
      clientReady: {
        title: 'Stores & representatives',
        description:
          'View stores and their assigned representatives, territories and districts.',
      },
      workflows: {
        title: 'Payee onboarding',
        description:
          'Invite stores, collect payee details and track enrolment progress.',
      },

      visibility: {
        title: 'Payment management',
        description:
          'Review EFT and cheque preferences, banking details and payee approval status.',
      },
    },

   securePlatform:
  'Access store and payment information based on your assigned role.',

    secureAccess:
      "Access is protected by organizational credentials and role-based permissions.",

    copyright: "© {{year}} Paygos - Mars. All rights reserved.",
  },

  forgotPasswordPage: {
    badge: "Password recovery",
    title: "Forgot your password?",
    description:
      "Enter your username and we'll send you instructions to reset your password.",
    successTitle: "Check your email",
    successMessage:
      "If an account exists for this username, password reset instructions have been sent.",
    securityMessage:
      "For your security, we won't confirm whether a username exists.",
  },
  resetPasswordPage: {
    badge: "Password reset",
    title: "Create a new password",
    description: "Enter and confirm your new password.",
    successTitle: "Password updated",
    successMessage: "Your password has been reset successfully.",
  },

  customerSignup: {
    page: {
      brandingBadge: "Secure customer onboarding",
      brandingTitle: "Welcome to Mars",
      brandingDescription:
        "Complete your account setup to securely access your store information, payments, and services.",
      brandingFooter: "Secure Mars customer onboarding",
      benefits: {
        secureAccessTitle: "Secure account access",
        secureAccessDescription:
          "Create your personal Mars credentials using your verified invitation.",
        storeInformationTitle: "Your store information",
        storeInformationDescription:
          "Access the customer profile and information associated with your account.",
        simpleOnboardingTitle: "Simple onboarding",
        simpleOnboardingDescription:
          "Complete your account setup once and continue directly into the Mars platform.",
        connectedPlatformTitle: "One connected platform",
        connectedPlatformDescription:
          "Manage customer activity, payments, and future services from one place.",
      },
      loadingTitle: "Loading your invitation",
      loadingDescription:
        "Please wait while we verify your customer information.",
      invitationErrorTitle: "Unable to continue",
      invitationErrorDescription:
        "Your invitation could not be loaded. The invitation may be invalid or expired.",
      successBadge: "Account setup complete",
      successTitle: "Account created",
      successDescription:
        "Your Mars account has been created successfully. You can now sign in.",
      usernameLabel: "Your username",
      setupBadge: "Customer account setup",
      setupTitle: "Complete your account setup",
      setupDescription:
        "Review your customer information and create your secure Mars login credentials.",
      customerInformationTitle: "Customer information",
      customerInformationDescription:
        "Confirm the store details linked to this invitation.",
      createLoginTitle: "Create your login",
      createLoginDescription: "Choose your name, username and password.",
      createAccountErrorTitle: "Unable to create account",
      unexpectedError: "An unexpected error occurred. Please try again.",
      secureAccountTitle: "Secure account creation",
      secureAccountDescription:
        "This signup page is available only through your customer invitation. Your password is securely managed by Mars authentication.",
      copyright: "© {{year}} Mars. All rights reserved.",
    },
    customerInformation: {
      customerName: "Customer Name",
      customerCode: "Customer Code",
      contactNote:
        "This information was provided during your customer setup. If anything is incorrect, please contact your sales representative.",
    },
    accountSetup: {
      passwordRequirementsTitle: "Password requirements",
      passwordRequirementsDescription: "Use at least 12 characters.",
      createAccount: "Create account",
      creatingAccount: "Creating account...",
    },
  },

  userSearchPage: {
    title: "Users",
    description: "Search and manage application users.",
    searchLabel: "Search users",
    searchPlaceholder: "Search by username, email or name",
    noUsers: "No users found.",
    totalUsers: "{{count}} users",
  },

  addUserPage: {
    title: "Add User",
    description: "Create a new application user and assign their role.",
    selectRole: "Select a role",
    passwordHint:
      "A temporary password has been generated. You can edit or regenerate it before creating the user.",
  },

  userDetail: {
    title: "User Details",
    description: "View user information and manage account access.",
    invalidUser: "The requested user is invalid.",

    accountInformation: {
      title: "Account Information",
      description: "Identity, role and account status information.",
    },

    access: {
      title: "Account Access",
      unlockedDescription:
        "This account is currently unlocked. Locking the account will prevent the user from logging in to the application.",
      lockedDescription:
        "This account is currently locked. Unlocking the account will allow the user to log in to the application again.",
      sessionNote:
        "Locking an account does not terminate an already authenticated session. Existing session enforcement will be handled separately.",
    },
  },

  storeDetail: {
    tabsLabel: "Store details",
    storeCode: "Store code",
    storeInformation: "Store information",
    customerType: "Customer type",
    parentStore: "Parent store",
    primaryAddress: "Primary address",
    city: "City",
    province: "Province",
    postalCode: "Postal code",
    addressType: "Address type",
    noAddress: "No address available.",
    territoryCode: "Territory code",
    district: "District",
    noTerritory: "No territory assigned.",
    paymentInformation: "Payment information",
    payeeStatus: "Payee status",
    noPaymentInformation: "No payment information available.",
    paymentHistoryPending: "Payment history will be displayed here.",
    sendInvitation: "Send invitation",
    sendInvitationDescription:
      "Enter the email address where the customer invitation should be sent.",
    sendingInvitation: "Sending...",
    invitationEmailPlaceholder: "customer@example.com",
    invitationFailed: "Unable to send the invitation. Please try again.",
    digits: "{{field}} must contain exactly {{count}} digits.",
    overview: "Overview",
    payee: "Payee",
    payments: "Payments",
    activity: "Activity",
    summary: "Store summary",
    payeeDetails: "Payee details",
    banking: "Banking details",
    recentPayments: "Recent payments",
    timeline: "Enrolment timeline",
    contact: "Payee contact",
    validation: "AFS validation",
    history: "Submission history",
    viewAll: "View all",
    approved: "Approved",
    submitted: "Submitted",
    passed: "Passed",
    pending: "Pending",
    payeeName: "Payee name",
    legalName: "Legal entity name",
    bank: "Bank",
    institution: "Institution number",
    transit: "Transit number",
    account: "Account number",
    lastFour: "Account last four digits",
    currency: "Currency",
    effective: "Effective from",
    masterAccount: "Master account",
    segment: "Segment",
    territory: "Territory",
    dsm: "DSM",
    paymentNumber: "Payment #",
    type: "Type",
    issued: "Issued",
    amount: "Amount",
    status: "Status",
    cleared: "Cleared",
    cashed: "Cashed",
    date: "Date",
    event: "Event",
    by: "By",
    paidYear: "Paid in {{year}}",
    average: "Average",
    nextRun: "Next payment run",
    settles: "Funds settle within two business days.",
    noScheduled: "No payment run scheduled.",
    noPayee: "No payee details submitted",
    noPayeeHint: "Payee details will appear after the store submits them.",
    noPayments: "No payments found",
    noPaymentsHint: "No payments match this year and payment type.",
    noHistory: "No submissions yet",
    activityHint: "Everything that has happened on this store record.",
    eventTypes: "Event types",
    enrolment: "Enrolment",
    payeeChanges: "Payee changes",
    invitations: "Invitations",
    year: "Payment year",
    verified: "Bank details verified against AFS on {{date}}.",
    notVerified: "Validation is pending.",
    detailsPending:
      "Store information from search. Payee, payment and activity details are not available yet.",
    demo: "Demo data · changes last until this page is reloaded.",
    reminderDescription:
      "Record a demo reminder for the store contact below. The original invitation link remains valid. No email will be sent.",
    lastReminder: "Last reminder",
    never: "None",
    daysAgo: "This store was last reminded {{count}} days ago.",
    reminderSaved: "Demo reminder recorded. No email was sent.",
    payeeSaved: "Demo payee update submitted for review.",

    demoForm:
      "Use dummy details only. This demo does not update a real bank account.",
    noInvitation: "This store has not been invited yet.",
    paymentReleased: "EFT payment released",
    payeeApproved: "Payee approved",
    bankingSubmitted: "Banking details submitted",
    signedIn: "Store signed in",
    invitationSent: "Invitation sent",
    chequeIssued: "Cheque issued",
    storeAdded: "Store added to territory",
    reminderRecorded: "Demo reminder recorded",
    payeeUpdated: "Payee update submitted",
    storeActor: "Store",
    systemActor: "System",
    demoActor: "Demo user",
    bankingDescription: "Current payment information for this payee",
    name: "Name",
  },

  storeSearch: {
    filtersError: "Filter options could not be loaded",

    summaryError: "Store totals could not be loaded",
    filtersUnavailable: "Additional filters and sorting are not available yet.",
    enrolled: "Onboarding Complete",
    customerType: "Customer type",
    independent: "Independent",
    group: "Group",
    selectedFilters: "Selected filters",
    removeFilter: "Remove {{filter}}",
    title: "Store Search",
    description: "Search stores in your territory and track enrolment status.",
    breadcrumb: "Stores & Payees",
    sample: "Sample data",
    invite: "Invite store",
    inviteUnavailable: "Store invitations are not available yet.",
    territory: "Stores in territory",
    territoryHint: "Territory F026",
    eft: "On EFT",
    eftHint: "{{percent}}% of your territory",
    invited: "Invitations pending",
    invitedHint: "Awaiting store sign-in",
    notInvited: "Not yet invited",
    notInvitedHint: "No invitation sent",
    all: "All",
    invitedTab: "Invitation Sent",
    notInvitedTab: "Not Invited",
    sort: "Order by",
    nameAsc: "Store name: A–Z",
    nameDesc: "Store name: Z–A",
    paymentNewest: "Last payment: newest",
    paymentOldest: "Last payment: oldest",
    invitedNewest: "Invited: newest",
    invitedOldest: "Invited: oldest",
    filter: "Filter",
    paymentFilter: "Payment method",
    allPayments: "All payment methods",
    cheque: "Cheque",
    noPayment: "Not set",
    store: "Store",
    status: "Status",
    payee: "Payee",
    payment: "Payment method",
    lastPayment: "Last payment",
    invitedOn: "Invited",
    empty: "No stores found",
    emptyHint: "Try another search or clear your filters.",
    clear: "Clear filters",
    details: "View details for {{name}}",
    storeCode: "Store code",
    location: "Location",
    close: "Close",
    results: "{{count}} stores found",
  },

  payee: {
    addNew: "Add New Payee",
    addDescription: "Add payee and payment information",
    personalInformation: "Personal Information",
    fullName: "Full Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    city: "City",
    province: "Province",
    postalCode: "Postal Code",
    paymentMethod: "Payment Method",
    eft: "EFT",
    cheque: "Cheque",
    bankingInformation: "Banking Information",
    bankInstitution: "Bank Institution",
    transitNumber: "Transit #",
    accountNumber: "Account #",
    bankingDocument: "Banking Document",

    createError: "Unable to add the payee. Please try again.",
  },

  navigation: {
    applicationName: "MARS",
    applicationDescription: "Payments Platform",

    menu: "Menu",
    dashboard: "Dashboard",
    uiComponents: "UI Components",
    users: "Users",
    eftPendingApproval: "EFT Pending Approval",
    mainNavigation: "Main navigation",
    addUser: "Add User",

    stores: {
      label: "Stores",
      storePayee: "Store Search",
      description: "Manage stores and their payees.",
      emptyTitle: "No stores available",
      emptyDescription:
        "Stores and payees will appear here after the API is connected.",
    },

    orders: {
      label: "Orders",
      all: "All Orders",
      new: "New Order",
      history: "Order History",
    },

    user: {
      guest: "Guest User",
      signIn: "Sign in",
      signInToContinue: "Sign in to continue",
    },

    sidebar: {
      expand: "Expand sidebar",
      collapse: "Collapse sidebar",
      open: "Open navigation",
      close: "Close navigation",
    },

    theme: {
      light: "Switch to light theme",
      dark: "Switch to dark theme",
    },

    client: {
      development: "Development Client",
    },
  },

  validation: {
    required: "{{field}} is required.",
    invalidEmail: "Enter a valid email address.",
    minimumLength: "{{field}} must contain at least {{minimum}} characters.",
    maximumLength: "{{field}} cannot contain more than {{maximum}} characters.",
    invalidDate: "Enter a valid date.",
    invalidFileType: "This file type is not allowed.",
    fileTooLarge: "The file exceeds the maximum allowed size.",
    fileTooLargeWithLimit: "The file exceeds the {{size}} size limit.",
    maximumFiles_one: "A maximum of {{count}} file is allowed.",
    maximumFiles_other: "A maximum of {{count}} files is allowed.",
    passwordsDoNotMatch: "Passwords do not match.",
    userNameRequired: "Username is required.",
    emailRequired: "Email address is required.",
    emailInvalid: "Enter a valid email address.",
    firstNameRequired: "First name is required.",
    lastNameRequired: "Last name is required.",
    passwordRequired: "Password is required.",
    confirmPasswordRequired: "Confirm your password.",
    passwordMismatch: "Passwords do not match.",
    roleRequired: "Select a role.",
    passwordMinLength: "Password must be at least 12 characters.",
    passwordUppercase: "Password must contain at least one uppercase letter.",
    passwordLowercase: "Password must contain at least one lowercase letter.",
    passwordNumber: "Password must contain at least one number.",
    passwordSpecial: "Password must contain at least one special character.",
  },

  ui: {
    alertDialog: {
      instruction: "Choose an action below to continue.",
    },

    breadcrumbs: {
      label: "Breadcrumbs",
    },

    dialog: {
      close: "Close dialog",
    },

    password: {
      show: "Show password",
      hide: "Hide password",
    },

    select: {
      placeholder: "Select an option",
      openOptions: "Open options",
      noOptions: "No options available",
    },

    multiSelect: {
      placeholder: "Search and select",
      selectedOptions: "Selected options",
      noSelection: "No selected options",
      removeOption: "Remove {{label}}",
    },

    file: {
      browse: "Browse files",
      noneSelected: "No file selected",
      selected_one: "{{count}} file selected",
      selected_other: "{{count}} files selected",
      remove: "Remove {{fileName}}",
    },

    dropZone: {
      defaultLabel: "File upload",
      instructions:
        "Drag and drop files here, paste files, or choose them from your device.",
    },

    datePicker: {
      openCalendar: "Open calendar",
      previousMonth: "Previous month",
      nextMonth: "Next month",
    },

    loading: {
      default: "Loading",
      content: "Loading content",
      table: "Loading table",
      card: "Loading card",
      processing: "Processing…",
      retrying: "Retrying…",
    },

    emptyState: {
      defaultTitle: "No information available",
    },

    errorState: {
      alertTitle: "Error",
      defaultTitle: "Something went wrong",
      defaultDescription: "The requested information could not be loaded.",
    },

    notifications: {
      dismiss: "Dismiss",
    },

    userProfileMenu: {
      open: "Open user profile menu",

      menuLabel: "User profile options",

      profile: "My profile",

      profileDescription: "View and update your profile",

      settings: "Settings",

      settingsDescription: "Manage your account preferences",

      signOut: "Sign out",

      signingOut: "Signing out…",

      signOutDescription: "End your current session",
    },

    dataTable: {
      searchLabel: "Search table",
      searchPlaceholder: "Search…",
      actions: "Actions",
      export: "Export",
      exporting: "Exporting…",
      exportFailed: "The data could not be exported. Please try again.",
      selectAll: "Select all rows",
      emptyTitle: "No records found",

      pagination: {
        previous: "Previous",
        next: "Next",
        page: "Page {{current}} of {{total}}",
        pageSize: "Rows per page",
        summary: "Showing {{from}}–{{to}} of {{total}} records",
      },
    },
  },

  componentShowcase: {
    page: {
      eyebrow: "Design System",
      title: "Component Showcase",
      description:
        "Examples of the shared UI components available to TPM features.",
      openDialog: "Open Dialog",
      openAlert: "Open Alert",
    },

    textFields: {
      title: "Text Fields",
      description: "Text, email, password and validation examples.",

      email: {
        label: "Email address",
        placeholder: "user@example.com",
        description: "Used for account notifications.",
      },

      password: {
        label: "Password",
        placeholder: "Enter your password",
        description: "Use your TPM account password.",
      },

      accountCode: {
        label: "Account code",
        placeholder: "ACC-10001",
        error: "The account code does not exist.",
      },

      readOnly: {
        label: "Read-only value",
        value: "System generated value",
      },

      disabled: {
        label: "Disabled field",
        value: "Unavailable",
      },
    },

    selection: {
      title: "Selection Controls",
      description: "Checkbox and Select components with controlled state.",

      orderStatus: {
        label: "Order status",
        placeholder: "Select an order status",
        description: "Filter orders by their current status.",
      },

      selectedValue: "Selected value: {{value}}",

      rememberPreference: {
        label: "Remember my preference",
        description: "Store this filter preference on this device.",
      },

      includeInactive: {
        label: "Include inactive orders",
        description: "Show cancelled and deleted orders.",
      },

      requiredAgreement: {
        label: "Required agreement",
        error: "You must accept this option.",
      },

      disabledOption: "Disabled option",

      statuses: {
        draft: {
          label: "Draft",
          description: "Order has not been submitted.",
        },

        submitted: {
          label: "Submitted",
          description: "Order is waiting for processing.",
        },

        pendingApproval: {
          label: "Pending Approval",
          description: "Order requires manager approval.",
        },

        approved: {
          label: "Approved",
          description: "Order has completed approval.",
        },

        cancelled: {
          label: "Cancelled",
          description: "Order is no longer active.",
        },
      },
    },

    buttons: {
      title: "Buttons and Loading",
      description: "Button variants and loading states.",
      primary: "Primary",
      secondary: "Secondary",
      ghost: "Ghost",
      danger: "Danger",
      disabled: "Disabled",
      loadingOrders: "Loading orders",
      processingOrder: "Processing order",
      viewDialog: "View Dialog",
    },

    cards: {
      title: "Card Variants",
      description: "Cards can compose headers, content and footers.",

      configurationValid: {
        title: "Configuration valid",
        description: "The client configuration was loaded successfully.",
      },

      transparent: {
        title: "Transparent card",
        description: "Useful for grouping content without a visible surface.",
      },
    },

    advancedInputs: {
      title: "Advanced Inputs",
      description: "Input groups, dates, switches and radio selections.",

      orderAmount: "Order amount",
      orderDate: "Order date",

      paymentMethod: {
        label: "Payment method",

        invoice: {
          label: "Invoice",
          description: "Pay using the normal invoice process.",
        },

        creditCard: {
          label: "Credit Card",
          description: "Charge the saved company card.",
        },
      },

      notifications: {
        label: "Send notifications",
        description: "Email the account when the order changes.",
      },

      permissions: {
        label: "Permissions",
        description: "Select one or more permissions.",
        readOrders: "Read Orders",
        createOrders: "Create Orders",
        submitOrders: "Submit Orders",
        approveAsRsm: "Approve as RSM",
      },
    },

    fileUploads: {
      title: "File Uploads",
      description: "Standard file selection and drag-and-drop attachments.",

      supportingDocument: {
        label: "Supporting document",
        description: "PDF or image files.",
      },

      attachments: {
        label: "Order attachments",
        description: "Maximum 5 files, 10 MB each.",
      },
    },

    feedback: {
      title: "Feedback States",
      description: "Reusable states for queries, tables and feature pages.",

      empty: {
        title: "No orders found",
        description:
          "No orders matched the selected filters. Change the filters or create a new order.",
      },

      error: {
        title: "Orders could not be loaded",
        description:
          "The server did not return the requested orders. Check your connection and try again.",
      },
    },

    skeletons: {
      title: "Skeleton Loading",
      description:
        "Layout-preserving placeholders used while API data is loading.",

      basic: {
        title: "Basic Skeletons",
        description:
          "Individual placeholders for text, avatars, images and controls.",
      },

      loadingOrderSummary: "Loading order summary",

      loadingAccountDetails: "Loading account details",

      loadingOrders: "Loading orders",
    },

    dialog: {
      title: "Create Test Order",
      description:
        "This dialog demonstrates reusable form components inside a modal.",
      saveOrder: "Save Order",

      orderReference: {
        label: "Order reference",
        placeholder: "Enter a reference",
      },

      initialStatus: "Initial status",

      sendNotification: "Send notification after saving",
    },

    alertDialog: {
      title: "Delete draft order?",
      description:
        "This draft order and its line items will be permanently deleted. This operation cannot be undone.",
      confirm: "Delete Order",
      cancel: "Keep Order",
    },

    navigationComponents: {
      title: "Badges, Breadcrumbs and Tabs",
      description:
        "Status indicators and navigation patterns built with the shared UI components.",
    },

    badges: {
      title: "Badges",
      description:
        "Badge variants for statuses, categories and concise metadata.",
      variants: "Variants",
      withStatusDot: "Status indicators",
      sizes: "Sizes",
      withIcons: "With icons",
      neutral: "Neutral",
      pending: "Pending Approval",
      awaitingReview: "Awaiting Review",
      small: "Small",
      medium: "Medium",
      complete: "Complete",
      inProgress: "In Progress",
    },

    breadcrumbs: {
      title: "Breadcrumbs",
      description: "Display the current page within an application hierarchy.",
      simple: "Simple path",
      nested: "Nested path",
      longPath: "Longer path",
      orderReference: "Order ORD-10042",
    },

    tabs: {
      underline: {
        title: "Underline Tabs",
        description: "A lightweight tab style for primary page sections.",
      },

      pills: {
        title: "Pill Tabs",
        description:
          "A contained tab style suitable for filters and compact views.",
      },

      vertical: {
        title: "Vertical Tabs",
        description:
          "A vertical tab layout for configuration and settings pages.",
      },

      orderInformation: "Order information",
      orderFilters: "Order filters",
      configuration: "Configuration sections",

      details: "Order Details",
      attachments: "Attachments",
      history: "History",
      settings: "Settings",

      open: "Open",
      general: "General",
      users: "Users",
      approval: "Approval",

      content: {
        detailsTitle: "Order details",
        detailsDescription:
          "Review the account, distributor, order type and financial information.",

        attachmentsTitle: "Order attachments",
        attachmentsDescription:
          "Review files and supporting documents associated with this order.",

        historyTitle: "Order history",
        historyDescription:
          "Review status changes and other recorded order activity.",

        settingsDescription: "Settings are unavailable in this example.",

        openOrders:
          "This view contains orders that are currently open or awaiting action.",

        approvedOrders:
          "This view contains orders that completed the approval process.",

        cancelledOrders: "This view contains orders that are no longer active.",

        generalTitle: "General configuration",
        generalDescription:
          "Manage the basic client and order-management configuration.",

        usersTitle: "User access",
        usersDescription:
          "Manage users, roles and access to platform functionality.",

        approvalTitle: "Approval workflow",
        approvalDescription:
          "Configure automatic, one-step and two-step order approvals.",
      },
    },

    progressBars: {
      title: "Progress Bars",
      description:
        "Accessible progress indicators for loading, uploading and multi-step operations.",

      determinate: {
        title: "Determinate Progress",
        description: "Use when the current amount of progress is known.",
      },

      interactive: {
        title: "Interactive Progress",
        description: "A controlled progress bar updated by application state.",
      },

      sizes: {
        title: "Progress Bar Sizes",
        description: "Choose a size based on the prominence of the operation.",
      },

      indeterminate: {
        title: "Indeterminate Progress",
        description:
          "Use when an operation is active but its completion percentage is unknown.",
      },

      orderCompletion: "Order completion",

      fileUpload: "Uploading attachments",

      fileUploadDescription: "Uploading order documents to the server.",

      approvalSteps: "Approval workflow",

      stepsCompleted: "{{current}} of {{total}} steps",

      failedImport: "Import completed with errors",

      processingOrders: "Processing orders",

      decrease: "Decrease progress",

      increase: "Increase progress",

      small: "Small progress bar",

      medium: "Medium progress bar",

      large: "Large progress bar",

      loadingConfiguration: "Loading client configuration",

      submittingOrder: "Submitting order",

      submittingDescription:
        "Please keep this page open while the order is submitted.",

      backgroundProcessing: "Processing in the background",
    },

    notifications: {
      title: "Notifications",

      description:
        "Temporary status messages for successful operations, warnings, errors and background activity.",

      basic: {
        title: "Notification Types",

        description:
          "Display the standard notification variants used throughout the application.",

        guidance:
          "Notifications communicate temporary status updates without moving keyboard focus.",
      },

      async: {
        title: "Asynchronous Operations",

        description:
          "Update a loading notification when an operation succeeds or fails.",

        explanation:
          "The loading notification is updated in place so the user hears and sees one continuous operation.",
      },

      management: {
        title: "Notification Management",

        description:
          "Display persistent loading feedback or dismiss active notifications.",

        guidance:
          "Loading notifications remain visible until the application updates or dismisses them.",
      },

      actions: {
        showSuccess: "Show success",

        showInfo: "Show information",

        showWarning: "Show warning",

        showError: "Show error",

        simulateSuccess: "Simulate successful submission",

        simulateFailure: "Simulate failed submission",

        showLoading: "Show loading notification",

        dismissAll: "Dismiss all notifications",
      },

      messages: {
        success: "Order saved successfully.",

        info: "The order draft was updated with the latest information.",

        warning: "The order requires additional information before submission.",

        error: "The order could not be saved.",

        submitting: "Submitting order…",

        submitted: "Order submitted successfully.",

        submitFailed: "The order could not be submitted.",

        backgroundProcessing: "Processing orders in the background…",
      },
    },

    dataTables: {
      title: "Responsive Data Tables",

      description:
        "Sortable business tables with sticky action and key columns for reliable use on smaller screens.",

      responsiveHint:
        "Scroll horizontally to view additional columns. The action and identifying key columns remain visible.",

      actions: {
        view: "View",
        select: "Select",
        review: "Review",
      },

      columns: {
        orderNumber: "Order number",

        account: "Account",

        accountCode: "Account code",

        accountName: "Account name",

        orderType: "Order type",

        status: "Status",

        netAmount: "Net amount",

        createdOn: "Created on",

        city: "City",

        province: "Province",

        distributor: "Distributor",

        submittedBy: "Submitted by",

        approvalStep: "Approval step",

        waitingTime: "Waiting time",
      },

      status: {
        draft: "Draft",
        pending: "Pending approval",
        approved: "Approved",
        active: "Active",
        inactive: "Inactive",
      },

      orders: {
        title: "View and Navigate",

        description:
          "A standard business-record table with a View action, sortable columns and server-pagination layout.",

        tableLabel: "Example orders",

        scrollLabel: "Scrollable example orders table",

        viewOrder: "View order {{orderNumber}}",

        opened: "Opening order {{orderNumber}}.",

        exported: "The orders were exported successfully.",
        pagination: "Showing {{from}}–{{to}} of {{total}} orders",
      },

      accounts: {
        title: "Select a Record",

        description:
          "A selection table where the first column performs an explicit workflow action.",

        tableLabel: "Example accounts",

        scrollLabel: "Scrollable example accounts table",

        selectAccount: "Select account {{accountCode}}",

        selectedAccount: "Account {{accountCode}} is selected",

        selectedNotification: "Account {{accountCode}} was selected.",

        selectedSummary: "Selected: {{accountCode}} — {{accountName}}",

        noSelection: "Select an account from the table to continue.",
      },

      approvals: {
        title: "Narrow-Screen Behaviour",

        description:
          "A deliberately constrained table used to verify sticky columns and horizontal scrolling.",

        constrainedDescription:
          "This example is restricted to a narrow container. Scroll horizontally and confirm that Review and Order number stay visible.",

        tableLabel: "Example approval queue",

        scrollLabel: "Scrollable example approval queue",

        reviewOrder: "Review order {{orderNumber}}",

        opened: "Opening approval for order {{orderNumber}}.",

        waitingDays_one: "{{count}} day",

        waitingDays_other: "{{count}} days",
      },

      selection: {
        title: "Bulk Selection",

        description:
          "A checkbox action column that collects row selections for bulk operations.",

        tableLabel: "Example selectable accounts",

        selectedCount_one: "{{count}} account selected",

        selectedCount_other: "{{count}} accounts selected",
      },

      server: {
        title: "Server Pagination",

        description:
          "Sorting, search and paging are delegated to the server through one consolidated query callback.",

        tableLabel: "Example server-paginated orders",

        note: "The table emits one query object; the feature refetches and supplies the page plus the total row count.",
      },

      pagination: {
        previous: "Previous",
        next: "Next",
        rowsPerPage: "Rows per page",

        page: "Page {{current}} of {{total}}",

        showcaseOnly:
          "Pagination is displayed as an example in the component showcase.",
      },
    },
  },

  dashboard: {
    title: "Dashboard",
    description: "TPM order-management overview.",

    cards: {
      openOrders: "Open Orders",
      pendingApproval: "Pending Approval",
      submittedToday: "Submitted Today",
      activePromotions: "Active Promotions",
    },

    foundation: {
      title: "Project foundation",
      description:
        "The shell, router, query client, theme state and feature-first folders are working.",
    },
  },
} as const;

export type EnglishTranslation = typeof translation;

const englishLocale = {
  metadata: {
    code: "en",
    locale: "en-CA",
    displayName: "English",
    shortName: "EN",
  },

  translation,
} satisfies LocaleDefinition<EnglishTranslation>;

export default englishLocale;
